import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Menu, X } from "lucide-react";
import { Toaster } from "./ui/sonner";
import { Helmet } from "react-helmet";

const LOGO_SMALL = "/assets/logo_small.png";

const fallbackPlayers = [
  {
    athlete_shortname: "SOULÉ M.",
    team_name: "Roma",
    match: "TOR-ROM",
    event_unit_cost: 4.0,
    pos_short: "ATT"
  },
  {
    athlete_shortname: "PULISIC C.",
    team_name: "Milan",
    match: "MIL-LEC",
    event_unit_cost: 4.0,
    pos_short: "MID"
  },
  {
    athlete_shortname: "LEÃO R.",
    team_name: "Milan",
    match: "MIL-LEC",
    event_unit_cost: 3.5,
    pos_short: "ATT"
  },
  {
    athlete_shortname: "NKUNKU C.",
    team_name: "Milan",
    match: "MIL-LEC",
    event_unit_cost: 3.5,
    pos_short: "ATT"
  },
  {
    athlete_shortname: "DUVIKAS T.",
    team_name: "Como",
    match: "LAZ-COM",
    event_unit_cost: 3.0,
    pos_short: "ATT"
  },
  {
    athlete_shortname: "DOVBYK A.",
    team_name: "Roma",
    match: "TOR-ROM",
    event_unit_cost: 3.0,
    pos_short: "ATT"
  }
];

// Translations
const pageContent = {
  it: {
    nav: {
      signIn: "Accedi",
      register: "Registrati",
      menu: {
        rules: "Regolamento",
        community: "Community",
        topPlayers: "Top Players",
        playerValues: "Prezzi Giocatori",
        contact: "Contatti"
      }
    },
    footer: {
      rights: "Tutti i diritti riservati.",
      contact: "Domande o feedback?"
    },
    meta: {
      title: "Prezzi Giocatori | Fanteex",
      description: "Controlla i prezzi aggiornati dei giocatori di Fanteex per il matchday corrente. Scopri quanto costa ogni calciatore."
    },
    page: {
      title: "Prezzi Giocatori",
      descriptionLines: [
        "Qui trovi i prezzi aggiornati dei calciatori per il matchday corrente.",
        "I costi sono calcolati dall'algoritmo di Fanteex.",
        "Cerca e ordina per nome, team o costo."
      ],
      currentMatchday: "MATCHDAY CORRENTE",
      noActiveMatchday: "Nessun matchday attivo al momento.",
      table: {
        name: "Nome",
        team: "Squadra",
        match: "Match",
        cost: "Costo"
      }
    }
  },
  en: {
    nav: {
      signIn: "Sign In",
      register: "Register",
      menu: {
        rules: "Game Rules",
        community: "Community",
        topPlayers: "Top Players",
        playerValues: "Player Costs",
        contact: "Get in Touch"
      }
    },
    footer: {
      rights: "All rights reserved.",
      contact: "Questions or feedback?"
    },
    meta: {
      title: "Player Costs | Fanteex",
      description: "Check updated Fanteex player prices for the current matchday. Discover how much each player costs."
    },
    page: {
      title: "Player Costs",
      descriptionLines: [
        "Here you find updated player prices for the current matchday.",
        "Costs are calculated by the Fanteex algorithm.",
        "Search and sort by name, team or cost."
      ],
      currentMatchday: "CURRENT MATCHDAY",
      noActiveMatchday: "No active matchday at the moment.",
      table: {
        name: "Name",
        team: "Team",
        match: "Match",
        cost: "Cost"
      }
    }
  }
};

// Tech Background Component
const TechBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
    <div className="absolute top-[-20%] left-[-20%] w-[80vw] h-[80vw] bg-[#1a0b2e] rounded-full blur-[100px] opacity-80" />
    <div className="absolute bottom-[-20%] right-[-20%] w-[80vw] h-[80vw] bg-[#0f0518] rounded-full blur-[100px] opacity-90" />
    <div className="absolute inset-0 bg-[linear-gradient(rgba(93,253,203,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(93,253,203,0.03)_1px,transparent_1px)] bg-[size:100px_100px]" />
  </div>
);

const PlayerCostsPage = () => {
  const [lang, setLang] = useState('it');
  const [menuOpen, setMenuOpen] = useState(false);
  const [players, setPlayers] = useState([]);
  const [eventUnitId, setEventUnitId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "cost", direction: "desc" });

  const t = pageContent[lang];

  const toggleLanguage = () => {
    setLang(prev => prev === 'it' ? 'en' : 'it');
  };

  const toggleMenu = () => {
    setMenuOpen(prev => !prev);
  };

  const menuItems = [
    { label: t.nav.menu.rules, path: "/rules" },
    { label: t.nav.menu.community, path: "/community" },
    { label: t.nav.menu.topPlayers, path: "/top-players" },
    { label: t.nav.menu.playerValues, path: "/players-costs" },
    { label: t.nav.menu.contact, path: "/#contact" }
  ];

  useEffect(() => {
    let isMounted = true;

    const loadPlayerCosts = async () => {
      try {
        setIsLoading(true);
        setLoadError("");
        const isLocalhost = window.location.hostname === "localhost";
        const apiUrl = isLocalhost
          ? "http://localhost:8000/api/current-event-prices"
          : "https://app.fanteex.com/api/current-event-prices";
        const response = await fetch(apiUrl);
        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }
        const data = await response.json();
        if (isMounted) {
          setEventUnitId(data.event_unit_id);
          const list = Array.isArray(data.players) ? data.players : [];
          setPlayers(list);
        }
      } catch (error) {
        if (isMounted) {
          setLoadError(error?.message || "Errore di caricamento.");
          setEventUnitId(1);
          setPlayers(fallbackPlayers);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadPlayerCosts();

    return () => {
      isMounted = false;
    };
  }, []);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredPlayers = players.filter((player) => {
    if (!normalizedSearch) {
      return true;
    }
    const name = (player.athlete_shortname || "").toLowerCase();
    const team = (player.team_name || "").toLowerCase();
    const match = (player.match || "").toLowerCase();
    return name.includes(normalizedSearch) || team.includes(normalizedSearch) || match.includes(normalizedSearch);
  });

  const sortDefaults = {
    name: "asc",
    team: "asc",
    match: "asc",
    cost: "desc"
  };

  const toggleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: sortDefaults[key] || "asc" };
    });
  };

  const sortedPlayers = [...filteredPlayers].sort((a, b) => {
    const direction = sortConfig.direction === "asc" ? 1 : -1;
    if (sortConfig.key === "name") {
      const nameA = (a.athlete_shortname || "").toLowerCase();
      const nameB = (b.athlete_shortname || "").toLowerCase();
      return nameA.localeCompare(nameB) * direction;
    }
    if (sortConfig.key === "team") {
      const teamA = (a.team_name || "").toLowerCase();
      const teamB = (b.team_name || "").toLowerCase();
      return teamA.localeCompare(teamB) * direction;
    }
    if (sortConfig.key === "match") {
      const matchA = (a.match || "").toLowerCase();
      const matchB = (b.match || "").toLowerCase();
      return matchA.localeCompare(matchB) * direction;
    }
    if (sortConfig.key === "cost") {
      const costA = Number(a.event_unit_cost ?? -Infinity);
      const costB = Number(b.event_unit_cost ?? -Infinity);
      return (costA - costB) * direction;
    }
    return 0;
  });

  const renderSortIndicator = (key) => {
    if (sortConfig.key !== key) {
      return "↕";
    }
    return sortConfig.direction === "asc" ? "▲" : "▼";
  };

  const roleInitials = {
    GK: { it: "P", en: "G" },
    DEF: { it: "D", en: "D" },
    MID: { it: "C", en: "M" },
    ATT: { it: "A", en: "A" }
  };

  const normalizePosition = (value) => {
    if (!value) {
      return "";
    }
    const raw = String(value).trim().toUpperCase();
    const map = {
      G: "GK",
      D: "DEF",
      M: "MID",
      A: "ATT",
      F: "ATT",
      GOALKEEPER: "GK",
      PORTIERE: "GK",
      DEFENDER: "DEF",
      DIFENSORE: "DEF",
      MIDFIELDER: "MID",
      CENTROCAMPISTA: "MID",
      FORWARD: "ATT",
      STRIKER: "ATT",
      ATTACKER: "ATT",
      ATTACCANTE: "ATT"
    };
    return map[raw] || raw;
  };

  const getRoleInitial = (player) => {
    const normalized = normalizePosition(player.pos_short || player.position || "");
    return roleInitials[normalized]?.[lang] || "-";
  };

  const getRoleDotClass = (player) => {
    const normalized = normalizePosition(player.pos_short || player.position || "");
    const map = {
      GK: "bg-amber-400 text-black",
      DEF: "bg-sky-400 text-black",
      MID: "bg-emerald-400 text-black",
      ATT: "bg-rose-400 text-black"
    };
    return map[normalized] || "bg-white/20 text-white";
  };

  const formatCost = (cost) => {
    if (cost === null || cost === undefined) return "-";
    return Number(cost).toFixed(1);
  };

  const getTeamInitials = (teamName) => {
    if (!teamName) return "";
    const parts = teamName.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].substring(0, 3).toUpperCase();
    }
    return parts.map(p => p[0]).join("").substring(0, 3).toUpperCase();
  };

  const renderMatchWithBoldTeam = (match, teamName) => {
    if (!match || !teamName) return match || "-";
    const teamInitials = getTeamInitials(teamName);
    const parts = match.split("-").map(p => p.trim());

    return (
      <>
        {parts.map((part, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && "-"}
            {part === teamInitials ? <span className="font-bold">{part}</span> : part}
          </React.Fragment>
        ))}
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>{t.meta.title}</title>
        <meta name="description" content={t.meta.description} />
        <link rel="canonical" href="https://fanteex.com/players-costs" />
      </Helmet>

      <div className="relative min-h-screen bg-fanteex-dark overflow-x-hidden flex flex-col font-sans selection:bg-[#5DFDCB] selection:text-fanteex-dark">
        {/* Background */}
        <div className="fixed inset-0 z-0 pointer-events-none bg-[#0f0518]">
          <div className="absolute inset-0 opacity-15 mix-blend-overlay" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/noise.svg)` }}></div>
        </div>

        <TechBackground />

        {/* Navigation */}
        <nav className="fixed top-0 w-full z-50 px-6 py-6 transition-all duration-300 backdrop-blur-md border-b border-white/5">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={toggleMenu}
                className="text-white/70 hover:text-[#5DFDCB] transition-colors p-2"
                aria-label="Menu"
              >
                {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>

              <Link to="/" className="flex items-center gap-3 cursor-pointer group">
                <img src={LOGO_SMALL} alt="FANTEEX" className="h-8 md:h-10 w-auto group-hover:drop-shadow-[0_0_8px_#5DFDCB] transition-all" />
              </Link>
            </div>

            <div className="flex items-center gap-3 md:gap-4">
              <a href="https://app.fanteex.com/signin.html">
                <button className="bg-transparent border border-white/30 text-white px-4 md:px-6 py-2 rounded-full font-display font-bold uppercase text-xs md:text-sm tracking-wider hover:border-[#5DFDCB] hover:text-[#5DFDCB] transition-all duration-300">
                  {t.nav.signIn}
                </button>
              </a>
              <a href="https://app.fanteex.com/register.html" className="hidden md:block">
                <button className="bg-[#5DFDCB] text-black px-4 md:px-6 py-2 rounded-full font-display font-black uppercase text-xs md:text-sm tracking-wider hover:shadow-[0_0_20px_#5DFDCB] transition-all duration-300">
                  {t.nav.register}
                </button>
              </a>
            </div>
          </div>
        </nav>

        {/* Slide-out Menu */}
        <AnimatePresence>
          {menuOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={toggleMenu}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
              />

              <motion.div
                initial={{ x: "-100%" }}
                animate={{ x: 0 }}
                exit={{ x: "-100%" }}
                transition={{ type: "tween", duration: 0.3 }}
                className="fixed top-0 left-0 h-full w-72 md:w-80 bg-[#0f0518]/95 backdrop-blur-xl border-r border-[#5DFDCB]/20 z-50 flex flex-col"
              >
                <div className="flex items-center justify-between p-6 border-b border-white/10">
                  <img src={LOGO_SMALL} alt="FANTEEX" className="h-8 w-auto" />
                  <button onClick={toggleMenu} className="text-white/70 hover:text-[#5DFDCB] transition-colors">
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="flex-1 py-6">
                  {menuItems.map((item, index) => (
                    <Link
                      key={index}
                      to={item.path}
                      onClick={toggleMenu}
                      className="block px-6 py-4 text-white/80 hover:text-[#5DFDCB] hover:bg-white/5 font-display font-bold uppercase tracking-wider text-sm transition-all border-l-2 border-transparent hover:border-[#5DFDCB]"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>

                <div className="p-6 border-t border-white/10">
                  <button
                    onClick={toggleLanguage}
                    className="flex items-center gap-2 text-white/70 hover:text-[#5DFDCB] transition-colors font-display font-bold uppercase text-sm tracking-wider"
                  >
                    <Globe className="w-4 h-4" />
                    <span>{lang === 'it' ? 'English' : 'Italiano'}</span>
                  </button>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Page Content */}
        <main className="flex-1 pt-28 pb-16 px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <motion.h1
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-6xl font-display font-black text-white uppercase mb-4 text-center"
            >
              {t.page.title}
            </motion.h1>
            <div className="text-center text-white/70 font-sans text-sm md:text-base mb-8 space-y-2">
              {t.page.descriptionLines.map((line, index) => (
                <p key={index}>{line}</p>
              ))}
            </div>

            {/* Current Matchday Badge */}
            {eventUnitId !== null && (
              <div className="flex justify-center mb-6">
                <div className="inline-flex items-center gap-3 bg-[#5DFDCB]/10 backdrop-blur-xl border border-[#5DFDCB]/40 rounded-full px-6 py-3">
                  <span className="text-[#5DFDCB] font-display font-bold uppercase text-xs tracking-wider">
                    {t.page.currentMatchday}
                  </span>
                  <span className="text-white font-display font-black text-2xl">
                    {eventUnitId}
                  </span>
                </div>
              </div>
            )}

            <div className="bg-[#140623]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-4 md:p-6 mb-6">
              <div className="flex items-center gap-3 bg-[#1a0b2e] border border-[#5DFDCB]/30 rounded-xl px-4 py-3">
                <span className="text-[#5DFDCB]">🔎</span>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={lang === "it" ? "Cerca nome, squadra o match" : "Search name, team or match"}
                  className="w-full bg-transparent text-white/80 font-sans focus:outline-none placeholder:text-white/40"
                />
              </div>
            </div>

            <div className="bg-[#140623]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-4 md:p-6">
              {isLoading && (
                <p className="text-white/70 font-sans text-center py-8">
                  {lang === "it" ? "Caricamento prezzi..." : "Loading prices..."}
                </p>
              )}

              {!isLoading && loadError && (
                <p className="text-red-300 font-sans text-center py-8">
                  {lang === "it" ? "Errore nel caricamento dei dati." : "Failed to load data."} ({loadError})
                </p>
              )}

              {!isLoading && !loadError && eventUnitId === null && (
                <p className="text-white/60 font-sans text-center py-8">
                  {t.page.noActiveMatchday}
                </p>
              )}

              {!isLoading && eventUnitId !== null && (
                <div>
                  <div>
                    {loadError && (
                      <div className="mb-3 rounded-xl border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-[12px] text-amber-200 font-sans">
                        {lang === "it"
                          ? "Abbiamo problemi con la connessione ai dati, riprova più tardi. Questi sono dati di test."
                          : "We are having trouble connecting to live data, please try again later. These are test data."}
                      </div>
                    )}
                    {/* Desktop/Tablet Header */}
                    <div className="hidden sm:grid grid-cols-[32px_24px_1fr_120px_120px_60px] gap-3 text-[11px] sm:text-xs font-display font-bold uppercase text-white/60 border-b border-white/10 pb-2">
                      <div />
                      <div />
                      <button
                        type="button"
                        onClick={() => toggleSort("name")}
                        className="text-left hover:text-white transition-colors"
                      >
                        {t.page.table.name} {renderSortIndicator("name")}
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleSort("team")}
                        className="text-left hover:text-white transition-colors"
                      >
                        {t.page.table.team} {renderSortIndicator("team")}
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleSort("match")}
                        className="text-left hover:text-white transition-colors"
                      >
                        {t.page.table.match} {renderSortIndicator("match")}
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleSort("cost")}
                        className="text-right text-[#5DFDCB] hover:text-white transition-colors"
                      >
                        {t.page.table.cost} {renderSortIndicator("cost")}
                      </button>
                    </div>
                    {/* Mobile Header */}
                    <div className="flex sm:hidden items-center text-[13px] font-display font-bold uppercase text-white/60 border-b border-white/10 pb-2">
                      <button
                        type="button"
                        onClick={() => toggleSort("name")}
                        className="text-left hover:text-white transition-colors min-w-0"
                      >
                        {t.page.table.name} {renderSortIndicator("name")}
                      </button>
                      <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
                        <div className="w-[24px]"></div>
                        <button
                          type="button"
                          onClick={() => toggleSort("cost")}
                          className="text-right text-[#5DFDCB] hover:text-white transition-colors min-w-[50px]"
                        >
                          {t.page.table.cost} {renderSortIndicator("cost")}
                        </button>
                      </div>
                    </div>

                    {sortedPlayers.map((player, index) => (
                      <div
                        key={`${player.athlete_shortname}-${index}`}
                        className="border-b border-white/5 py-3 sm:py-4"
                      >
                        {/* Desktop/Tablet Row */}
                        <div className="hidden sm:grid grid-cols-[32px_24px_1fr_120px_120px_60px] gap-3 items-center">
                          <div className="flex items-center justify-center">
                            {player.picture ? (
                              <img
                                src={player.picture}
                                alt={player.athlete_shortname || "Player"}
                                className="w-7 h-7 sm:w-9 sm:h-9 rounded-full object-cover border border-[#5DFDCB]/30"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-white/10 border border-[#5DFDCB]/20" />
                            )}
                          </div>
                          <div className="flex items-center justify-center">
                            <span
                              className={`w-[20px] h-[20px] sm:w-[24px] sm:h-[24px] rounded-full flex items-center justify-center text-[9px] sm:text-[10px] font-display font-bold ${getRoleDotClass(player)}`}
                            >
                              {getRoleInitial(player)}
                            </span>
                          </div>
                          <div className="text-white font-display font-black uppercase tracking-wide text-xs sm:text-sm truncate">
                            {player.athlete_shortname || "-"}
                          </div>
                          <div className="text-white/70 font-sans text-[11px] sm:text-sm">
                            {player.team_name || "-"}
                          </div>
                          <div className="text-white/70 font-sans text-[11px] sm:text-sm">
                            {renderMatchWithBoldTeam(player.match, player.team_name)}
                          </div>
                          <div className="text-white font-display font-bold text-right text-[11px] sm:text-sm">
                            {formatCost(player.event_unit_cost)}
                          </div>
                        </div>
                        {/* Mobile Row */}
                        <div className="flex sm:hidden items-center gap-3">
                          <div className="flex items-center gap-2 min-w-0" style={{flex: "1 1 0"}}>
                            {player.picture ? (
                              <img
                                src={player.picture}
                                alt={player.athlete_shortname || "Player"}
                                className="w-9 h-9 rounded-full object-cover border border-[#5DFDCB]/30 flex-shrink-0"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-white/10 border border-[#5DFDCB]/20 flex-shrink-0" />
                            )}
                            <div className="min-w-0" style={{flex: "1 1 0"}}>
                              <div className="text-white font-display font-normal uppercase tracking-wide truncate text-[14px] leading-tight">
                                {player.athlete_shortname || "-"}
                              </div>
                              <div className="text-white/50 font-sans text-[11px] truncate">
                                {renderMatchWithBoldTeam(player.match, player.team_name)}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2" style={{flex: "0 0 auto", minWidth: "80px"}}>
                            <span
                              className={`w-[24px] h-[24px] rounded-full flex items-center justify-center text-[10px] font-display font-bold ${getRoleDotClass(player)}`}
                            >
                              {getRoleInitial(player)}
                            </span>
                            <div className="text-white font-display font-bold text-right text-[15px]" style={{width: "50px"}}>
                              {formatCost(player.event_unit_cost)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {sortedPlayers.length === 0 && (
                      <p className="text-white/60 font-sans text-center py-10">
                        {lang === "it" ? "Nessun risultato." : "No results."}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>

        <Toaster position="bottom-center" theme="dark" />
      </div>
    </>
  );
};

export default PlayerCostsPage;
